import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import { useFeedback } from '../contexts/FeedbackContext';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import { track } from '../utils/analytics';
import { isInvalidated, clearInvalidation, setLastFetchedAt, getLastFetchedAt, isStale, invalidateDashboardPlans, setCachedPlans, getCachedPlans } from '../utils/dashboardInvalidation';
import ProtectedRoute from '../components/ProtectedRoute';
import NavBar from '../components/NavBar';
import DashboardSidebar from '../components/DashboardSidebar';
import styles from './Dashboard.module.css';

// Delete Confirmation Modal Component
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, planTitle, language }) => {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
          maxWidth: '480px',
          width: '90%',
          padding: '0',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '24px 24px 16px 24px',
          borderBottom: '1px solid #E2E3E9'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '1.5rem',
            fontFamily: 'Baloo 2',
            fontWeight: '600',
            color: '#0D5449',
            textTransform: 'capitalize'
          }}>
            {t('delete_lesson_plan', language) || 'Delete Lesson Plan'}
          </h2>
        </div>

        {/* Body */}
        <div style={{
          padding: '24px',
        }}>
          <p style={{
            margin: 0,
            fontSize: '1rem',
            fontFamily: 'Baloo 2',
            color: '#666',
            lineHeight: '1.5',
            marginBottom: '8px'
          }}>
            {t('delete_confirmation_message', language) || 'Are you sure you want to delete this lesson plan?'}
          </p>
          <p style={{
            margin: 0,
            fontSize: '0.875rem',
            fontFamily: 'Baloo 2',
            color: '#999',
            fontStyle: 'italic',
            fontWeight: '500'
          }}>
            "{planTitle}"
          </p>
          <p style={{
            margin: '16px 0 0 0',
            fontSize: '0.875rem',
            fontFamily: 'Baloo 2',
            color: '#DC2626',
            fontWeight: '500'
          }}>
            {t('delete_warning', language) || 'This action cannot be undone.'}
          </p>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px 24px 24px',
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end',
          borderTop: '1px solid #E2E3E9'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: '1px solid #E2E3E9',
              background: '#F8F9FA',
              color: '#6c757d',
              fontSize: '0.875rem',
              fontFamily: 'Azeret Mono',
              fontWeight: '600',
              textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'all 0.2s',
              minWidth: '100px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#E9ECEF';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#F8F9FA';
            }}
          >
            {t('cancel', language) || 'Cancel'}
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              background: '#DC2626',
              color: 'white',
              fontSize: '0.875rem',
              fontFamily: 'Azeret Mono',
              fontWeight: '600',
              textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'all 0.2s',
              minWidth: '100px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#B91C1C';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#DC2626';
            }}
          >
            {t('delete', language)}
          </button>
        </div>
      </div>
    </div>
  );
};

// New Lesson Plan Card Component matching design
const LessonPlanCard = ({ plan, onClick, onDeleteClick, disabled }) => {
  const { language } = useLanguage();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);
  
  // Determine language tag from plan data (you may need to adjust this based on your actual data structure)
  const getLanguageTag = () => {
    // This is a placeholder - adjust based on your actual data structure
    return 'ʻŌlelo';
  };

  // Handle three-dot menu click
  const handleMenuClick = (e) => {
    e.stopPropagation(); // Prevent card click
    setShowMenu(!showMenu);
  };

  // Handle delete click
  const handleDelete = (e) => {
    e.stopPropagation(); // Prevent card click
    setShowMenu(false);
    onDeleteClick(plan);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  return (
    <div 
      data-state="Default" 
      onClick={disabled ? undefined : onClick}
      style={{
        flex: '1 1 0', 
        minWidth: '280px',
        maxWidth: '100%',
        padding: '1rem', 
        background: '#FFFCF9', 
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)', 
        borderRadius: '0.75rem', 
        border: '1px solid #BFBBB9', 
        flexDirection: 'column', 
        justifyContent: 'flex-start', 
        alignItems: 'flex-start', 
        gap: '1rem', 
        display: 'flex',
        cursor: disabled ? 'wait' : 'pointer',
        opacity: disabled ? 0.8 : 1,
        transition: 'transform 0.2s, box-shadow 0.2s',
        position: 'relative'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0px 4px 12px rgba(0, 0, 0, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0px 2px 8px rgba(0, 0, 0, 0.1)';
      }}
    >
      <div style={{width: '100%', flexDirection: 'column', gap: '0.5rem', display: 'flex'}}>
        <div style={{width: '100%', justifyContent: 'space-between', alignItems: 'center', display: 'flex'}}>
          <div style={{flex: 1, color: 'black', fontSize: '0.875rem', fontFamily: 'Baloo 2', fontWeight: '400', wordWrap: 'break-word'}}>
            {plan.standard || t('standard_code', language)}
          </div>
          <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', position: 'relative'}}>
            {/* Three-dot menu button */}
            <div 
              onClick={handleMenuClick}
              style={{
                width: '1.5rem', 
                height: '1.5rem', 
                position: 'relative', 
                flexShrink: 0, 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <div style={{
                width: '4px',
                height: '4px',
                borderRadius: '50%',
                background: '#696A6E',
                boxShadow: '0 -6px 0 #696A6E, 0 6px 0 #696A6E'
              }} />
            </div>
            
            {/* Dropdown menu */}
            {showMenu && (
              <div
                ref={menuRef}
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: '0',
                  marginTop: '0.25rem',
                  background: 'white',
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                  borderRadius: '0.5rem',
                  border: '1px solid #E2E3E9',
                  minWidth: '120px',
                  zIndex: 1000,
                  padding: '0.25rem 0'
                }}
              >
                <div
                  onClick={handleDelete}
                  style={{
                    padding: '0.5rem 1rem',
                    cursor: 'pointer',
                    color: '#DC2626',
                    fontSize: '0.875rem',
                    fontFamily: 'Baloo 2',
                    fontWeight: '400',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#FEF2F2'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  {t('delete', language)}
                </div>
              </div>
            )}
          </div>
        </div>
        <div style={{width: '100%', color: 'black', fontSize: '1.5rem', fontFamily: 'Baloo 2', fontWeight: '500', lineHeight: '1.3', wordWrap: 'break-word'}}>
          {plan.title || t('understanding_computer_parts', language)}
        </div>
        <div style={{width: '100%', color: '#666', fontSize: '0.875rem', fontFamily: 'Baloo 2', fontWeight: '400', lineHeight: '1.4', wordWrap: 'break-word'}}>
          {plan.standard_description || plan.description || t('lesson_description', language)}
        </div>
      </div>
      <div style={{width: '100%', justifyContent: 'flex-start', alignItems: 'flex-start', gap: '0.5rem', display: 'flex', flexWrap: 'wrap'}}>
        {/* Grade Tag */}
        <div data-property-1="Info" style={{
          paddingLeft: '0.5rem', 
          paddingRight: '0.5rem', 
          paddingTop: '0.25rem', 
          paddingBottom: '0.25rem', 
          background: '#F2F3F8', 
          borderRadius: '0.25rem', 
          border: '1px solid #E2E3E9', 
          justifyContent: 'center', 
          alignItems: 'center', 
          display: 'flex'
        }}>
          <div style={{
            color: '#696A6E', 
            fontSize: '0.75rem', 
            fontFamily: 'Azeret Mono', 
            fontWeight: '600', 
            textTransform: 'uppercase', 
            lineHeight: '1', 
            whiteSpace: 'nowrap'
          }}>
            {plan.grade || 'K-2'}
          </div>
        </div>
        {/* Language Tag */}
        <div data-property-1="Info" style={{
          paddingLeft: '0.5rem', 
          paddingRight: '0.5rem', 
          paddingTop: '0.25rem', 
          paddingBottom: '0.25rem', 
          background: '#F2F3F8', 
          borderRadius: '0.25rem', 
          border: '1px solid #E2E3E9', 
          justifyContent: 'center', 
          alignItems: 'center', 
          display: 'flex'
        }}>
          <div style={{
            color: '#696A6E', 
            fontSize: '0.75rem', 
            fontFamily: 'Azeret Mono', 
            fontWeight: '600', 
            textTransform: 'uppercase', 
            lineHeight: '1', 
            whiteSpace: 'nowrap'
          }}>
            {getLanguageTag()}
          </div>
        </div>
        {/* Subject Tag */}
        <div data-property-1="Info" style={{
          paddingLeft: '0.5rem', 
          paddingRight: '0.5rem', 
          paddingTop: '0.25rem', 
          paddingBottom: '0.25rem', 
          background: '#F2F3F8', 
          borderRadius: '0.25rem', 
          border: '1px solid #E2E3E9', 
          justifyContent: 'center', 
          alignItems: 'center', 
          display: 'flex'
        }}>
          <div style={{
            color: '#696A6E', 
            fontSize: '0.75rem', 
            fontFamily: 'Azeret Mono', 
            fontWeight: '600', 
            textTransform: 'uppercase', 
            lineHeight: '1', 
            whiteSpace: 'nowrap'
          }}>
            {plan.subject || 'Computer Science'}
          </div>
        </div>
      </div>
    </div>
  );
};

// Quiz Card Component
const QuizCard = ({ quiz, onClick, onDeleteClick }) => {
  const { language } = useLanguage();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  // Handle three-dot menu click
  const handleMenuClick = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  // Handle delete click
  const handleDelete = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    onDeleteClick(quiz);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  return (
    <div 
      onClick={onClick}
      style={{
        flex: '1 1 0', 
        minWidth: '280px',
        maxWidth: '100%',
        padding: '1rem', 
        background: '#F9FAFF', 
        boxShadow: '0px 2px 8px rgba(99, 102, 241, 0.15)', 
        borderRadius: '0.75rem', 
        border: '1px solid #A5B4FC', 
        flexDirection: 'column', 
        justifyContent: 'flex-start', 
        alignItems: 'flex-start', 
        gap: '1rem', 
        display: 'flex',
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        position: 'relative'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0px 4px 12px rgba(99, 102, 241, 0.25)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0px 2px 8px rgba(99, 102, 241, 0.15)';
      }}
    >
      <div style={{width: '100%', flexDirection: 'column', gap: '0.5rem', display: 'flex'}}>
        <div style={{width: '100%', justifyContent: 'space-between', alignItems: 'center', display: 'flex'}}>
          <div style={{flex: 1, color: '#6366F1', fontSize: '0.875rem', fontFamily: 'Baloo 2', fontWeight: '500', wordWrap: 'break-word'}}>
            {quiz.standards && quiz.standards.length > 0 ? quiz.standards[0].standard : 'Quiz'}
          </div>
          <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', position: 'relative'}}>
            {/* Three-dot menu button */}
            <div 
              onClick={handleMenuClick}
              style={{
                width: '1.5rem', 
                height: '1.5rem', 
                position: 'relative', 
                flexShrink: 0, 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <div style={{
                width: '4px',
                height: '4px',
                borderRadius: '50%',
                background: '#6366F1',
                boxShadow: '0 -6px 0 #6366F1, 0 6px 0 #6366F1'
              }} />
            </div>
            
            {/* Dropdown menu */}
            {showMenu && (
              <div
                ref={menuRef}
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: '0',
                  marginTop: '0.25rem',
                  background: 'white',
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                  borderRadius: '0.5rem',
                  border: '1px solid #E2E3E9',
                  minWidth: '120px',
                  zIndex: 1000,
                  padding: '0.25rem 0'
                }}
              >
                <div
                  onClick={handleDelete}
                  style={{
                    padding: '0.5rem 1rem',
                    cursor: 'pointer',
                    color: '#DC2626',
                    fontSize: '0.875rem',
                    fontFamily: 'Baloo 2',
                    fontWeight: '400',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#FEF2F2'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  {t('delete', language)}
                </div>
              </div>
            )}
          </div>
        </div>
        <div style={{width: '100%', color: '#4F46E5', fontSize: '1.5rem', fontFamily: 'Baloo 2', fontWeight: '500', lineHeight: '1.3', wordWrap: 'break-word'}}>
          {quiz.title || 'Untitled Quiz'}
        </div>
        {quiz.description && (
          <div style={{width: '100%', color: '#666', fontSize: '0.875rem', fontFamily: 'Baloo 2', fontWeight: '400', lineHeight: '1.4', wordWrap: 'break-word'}}>
            {quiz.description}
          </div>
        )}
      </div>
      <div style={{width: '100%', justifyContent: 'flex-start', alignItems: 'flex-start', gap: '0.5rem', display: 'flex', flexWrap: 'wrap'}}>
        {/* Grade Tag */}
        {quiz.grade && (
          <div style={{
            paddingLeft: '0.5rem', 
            paddingRight: '0.5rem', 
            paddingTop: '0.25rem', 
            paddingBottom: '0.25rem', 
            background: '#EEF2FF', 
            borderRadius: '0.25rem', 
            border: '1px solid #C7D2FE', 
            justifyContent: 'center', 
            alignItems: 'center', 
            display: 'flex'
          }}>
            <div style={{
              color: '#6366F1', 
              fontSize: '0.75rem', 
              fontFamily: 'Azeret Mono', 
              fontWeight: '600', 
              textTransform: 'uppercase', 
              lineHeight: '1', 
              whiteSpace: 'nowrap'
            }}>
              {quiz.grade}
            </div>
          </div>
        )}
        {/* Subject Tag */}
        {quiz.subject && (
          <div style={{
            paddingLeft: '0.5rem', 
            paddingRight: '0.5rem', 
            paddingTop: '0.25rem', 
            paddingBottom: '0.25rem', 
            background: '#EEF2FF', 
            borderRadius: '0.25rem', 
            border: '1px solid #C7D2FE', 
            justifyContent: 'center', 
            alignItems: 'center', 
            display: 'flex'
          }}>
            <div style={{
              color: '#6366F1', 
              fontSize: '0.75rem', 
              fontFamily: 'Azeret Mono', 
              fontWeight: '600', 
              textTransform: 'uppercase', 
              lineHeight: '1', 
              whiteSpace: 'nowrap'
            }}>
              {quiz.subject === 'cs' ? 'CS' : quiz.subject === 'math' ? 'MATH' : quiz.subject.toUpperCase()}
            </div>
          </div>
        )}
        {/* AI Assisted Badge */}
        <div style={{
          paddingLeft: '0.5rem', 
          paddingRight: '0.5rem', 
          paddingTop: '0.25rem', 
          paddingBottom: '0.25rem', 
          background: '#EEF2FF', 
          borderRadius: '0.25rem', 
          border: '1px solid #C7D2FE', 
          justifyContent: 'center', 
          alignItems: 'center', 
          gap: '0.25rem',
          display: 'flex'
        }}>
          <img
            src="/assets/Solid/Interface/AI.svg"
            alt="AI Assisted"
            style={{ width: '0.875rem', height: '0.875rem', flexShrink: 0 }}
          />
          <div style={{
            color: '#6366F1', 
            fontSize: '0.75rem', 
            fontFamily: 'Azeret Mono', 
            fontWeight: '600', 
            textTransform: 'uppercase', 
            lineHeight: '1', 
            whiteSpace: 'nowrap'
          }}>
            {t('ai_assisted', language)}
          </div>
        </div>
      </div>
    </div>
  );
};

function DashboardContent() {
  const navigate = useNavigate();
  const { user, token, refreshUserProfile } = useAuth();
  const { openFeedbackPanel } = useFeedback();
  const { language } = useLanguage();
  const [inProgressPlans, setInProgressPlans] = useState([]);
  const [completedPlans, setCompletedPlans] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, plan: null });
  const [deleteQuizModal, setDeleteQuizModal] = useState({ isOpen: false, quiz: null });
  const [showNoGenerationsModal, setShowNoGenerationsModal] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);

  const handleDashboardFeedback = () => {
    openFeedbackPanel('screen', {
      page_id: 'dashboard',
      page_name: 'Dashboard',
      page_elements: ['Welcome message', 'Generations counter', 'Create lesson plan', 'My plans', 'Resources', 'About & Help'],
    });
  };

  // Filter and sort state
  const [filters, setFilters] = useState({
    subject: null,
    language: null,
    grade: null
  });
  const [sortBy, setSortBy] = useState('most_recent');
  const [showSubjectDropdown, setShowSubjectDropdown] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showGradeDropdown, setShowGradeDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  useEffect(() => {
    if (!token) return;
    const cacheKey = token;

    const mustRefetch = isInvalidated(cacheKey);
    const skipRefetch = !mustRefetch && getLastFetchedAt(cacheKey) > 0 && !isStale(cacheKey);

    if (skipRefetch) {
      const { inProgress, completed } = getCachedPlans(cacheKey);
      setInProgressPlans(inProgress);
      setCompletedPlans(completed);
      if (refreshUserProfile) {
        refreshUserProfile();
      }
      setLoading(false);
      return;
    }

    const fetchPlans = async () => {
      try {
        setLoading(true);

        // Refresh user profile in background (don't block plan render)
        if (refreshUserProfile) {
          refreshUserProfile();
        }

        // Fetch in-progress and completed plans in parallel for faster render
        const [inProgressRes, completedRes] = await Promise.all([
          fetch('/api/lesson-plans/in-progress/', {
            headers: {
              Authorization: `Token ${token}`,
              'Content-Type': 'application/json',
            },
          }),
          fetch('/api/lesson-plans/completed/', {
            headers: {
              Authorization: `Token ${token}`,
              'Content-Type': 'application/json',
            },
          }),
        ]);

        if (!inProgressRes.ok || !completedRes.ok) {
          throw new Error('Failed to fetch lesson plans');
        }

        const [inProgressData, completedData] = await Promise.all([
          inProgressRes.json(),
          completedRes.json(),
        ]);
        const inProgress = inProgressData.plans || [];
        const completed = completedData.plans || [];

        setInProgressPlans(inProgress);
        setCompletedPlans(completed);
        setCachedPlans(inProgress, completed, cacheKey);
        setLastFetchedAt(Date.now(), cacheKey);
        clearInvalidation(cacheKey);

        // Quizzes no longer displayed on home page – skip quiz API
        // const quizzesRes = await fetch('/api/quiz/list/', { ... });
        // if (quizzesRes.ok) { const quizzesData = await quizzesRes.json(); setQuizzes(quizzesData.quizzes || []); }
      } catch (e) {
        console.error('Failed to load plans', e);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, [token, refreshUserProfile]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('[data-filter-dropdown]') && !event.target.closest('[data-sort-dropdown]')) {
        setShowSubjectDropdown(false);
        setShowLanguageDropdown(false);
        setShowGradeDropdown(false);
        setShowSortDropdown(false);
      }
    };

    if (showSubjectDropdown || showLanguageDropdown || showGradeDropdown || showSortDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSubjectDropdown, showLanguageDropdown, showGradeDropdown, showSortDropdown]);

  /** Completed plans: go to view screen. */
  const handleCardClick = (plan) => {
    track('Viewed Lesson Plan', { lesson_plan_id: plan.id, screen_name: 'Dashboard' });
    navigate(`/view-lesson-plan?id=${plan.id}`);
  };

  /** In-progress plans: resume at the last step (cultural elements or create form). */
  const [resumingPlanId, setResumingPlanId] = useState(null);
  const handleInProgressCardClick = async (plan) => {
    track('Resume Lesson Plan', { lesson_plan_id: plan.id, screen_name: 'Dashboard' });
    setResumingPlanId(plan.id);
    try {
      const res = await fetch(`/api/lesson-plans/${plan.id}/resume/`, {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        navigate(`/view-lesson-plan?id=${plan.id}`);
        return;
      }
      const data = await res.json();
      const resumeData = {
        id: data.id,
        title: data.title,
        subject: data.subject,
        grade: data.grade,
        standard: data.standard,
        concept: data.concept,
        subconcept: data.subconcept,
        standard_description: data.standard_description,
        language: data.language,
        moku: data.moku,
        island: data.island,
        current_step: data.current_step,
        lesson_plan_data: data.lesson_plan_data || {},
      };
      const generatedElements = data.generated_elements;
      const hasElements = generatedElements && (generatedElements.story || generatedElements.proverb || generatedElements.cultural_practice);
      const canResumeToElements = hasElements && data.current_step !== 'create_lesson';

      if (canResumeToElements) {
        navigate('/select-elements', {
          state: {
            resumeData,
            isResuming: true,
            selectedSubject: data.subject,
            selectedGrade: data.grade,
            selectedConcept: data.concept,
            selectedSubconcept: data.subconcept,
            selectedStandardIdentifier: data.standard,
            selectedStandardDescription: data.standard_description,
            selectedLanguage: data.language,
            selectedMoku: data.moku,
            selectedIsland: data.island,
            generatedElements,
          },
        });
      } else {
        navigate('/create-lesson-plan/form', { state: { resumeData, isResuming: true } });
      }
    } catch (e) {
      console.error('Failed to resume lesson plan', e);
      navigate(`/view-lesson-plan?id=${plan.id}`);
    } finally {
      setResumingPlanId(null);
    }
  };

  const handleDeleteClick = (plan) => {
    setDeleteModal({ isOpen: true, plan });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.plan) return;

    const planId = deleteModal.plan.id;
    try {
      const response = await fetch(`/api/lesson-plans/${planId}/delete/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // Remove from state
        setCompletedPlans(prev => prev.filter(plan => plan.id !== planId));
        setInProgressPlans(prev => prev.filter(plan => plan.id !== planId));
        setDeleteModal({ isOpen: false, plan: null });
        invalidateDashboardPlans(token); // so next visit refetches if user navigates away and back
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to delete lesson plan');
      }
    } catch (error) {
      console.error('Error deleting lesson plan:', error);
      alert('Failed to delete lesson plan. Please try again.');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, plan: null });
  };

  const handleQuizCardClick = (quiz) => {
    // Navigate to view quiz screen (view-only mode)
    navigate(`/view-quiz/${quiz.id}`);
  };

  const handleQuizDeleteClick = (quiz) => {
    setDeleteQuizModal({ isOpen: true, quiz });
  };

  const handleQuizDeleteConfirm = async () => {
    if (!deleteQuizModal.quiz) return;

    const quizId = deleteQuizModal.quiz.id;
    try {
      const response = await fetch(`/api/quiz/${quizId}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // Remove from state
        setQuizzes(prev => prev.filter(quiz => quiz.id !== quizId));
        setDeleteQuizModal({ isOpen: false, quiz: null });
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to delete quiz');
      }
    } catch (error) {
      console.error('Error deleting quiz:', error);
      alert('Failed to delete quiz. Please try again.');
    }
  };

  const handleQuizDeleteCancel = () => {
    setDeleteQuizModal({ isOpen: false, quiz: null });
  };

  // Grade filter: High School includes "High School: <concept>"; Middle School includes "MS"
  const planMatchesGrade = (plan, gradeFilter) => {
    if (!gradeFilter) return true;
    if (gradeFilter === 'High School') {
      return plan.grade === 'High School' || plan.grade === 'HS' || (plan.grade && String(plan.grade).startsWith('High School:'));
    }
    if (gradeFilter === 'Middle School') {
      return plan.grade === 'Middle School' || plan.grade === 'MS';
    }
    return plan.grade === gradeFilter;
  };

  const filterPlans = (plans) => {
    return plans.filter(plan => {
      if (filters.subject && plan.subject !== filters.subject) return false;
      if (filters.language && plan.language !== filters.language) return false;
      if (filters.grade && !planMatchesGrade(plan, filters.grade)) return false;
      return true;
    });
  };

  // Sort function
  const sortPlans = (plans) => {
    const sorted = [...plans];
    switch (sortBy) {
      case 'most_recent':
        return sorted.sort((a, b) => {
          const dateA = new Date(a.updated_at || a.created_at);
          const dateB = new Date(b.updated_at || b.created_at);
          return dateB - dateA;
        });
      case 'oldest':
        return sorted.sort((a, b) => {
          const dateA = new Date(a.updated_at || a.created_at);
          const dateB = new Date(b.updated_at || b.created_at);
          return dateA - dateB;
        });
      case 'title_asc':
        return sorted.sort((a, b) => {
          const titleA = (a.title || '').toLowerCase();
          const titleB = (b.title || '').toLowerCase();
          return titleA.localeCompare(titleB);
        });
      case 'title_desc':
        return sorted.sort((a, b) => {
          const titleA = (a.title || '').toLowerCase();
          const titleB = (b.title || '').toLowerCase();
          return titleB.localeCompare(titleA);
        });
      case 'status':
        return sorted.sort((a, b) => {
          // Completed first, then in-progress
          if (a.status === 'completed' && b.status !== 'completed') return -1;
          if (a.status !== 'completed' && b.status === 'completed') return 1;
          return 0;
        });
      default:
        return sorted;
    }
  };

  // Get unique values for filter dropdowns
  const getAllPlans = () => [...completedPlans, ...inProgressPlans];
  const getUniqueSubjects = () => {
    const subjects = new Set(getAllPlans().map(p => p.subject).filter(Boolean));
    return Array.from(subjects);
  };
  
  // Predefined language options
  const getLanguageOptions = () => [
    { value: 'en', label: 'English' },
    { value: 'haw', label: 'ʻŌlelo Hawaiʻi' }
  ];
  
  // Grade options: K-12, Middle School, High School (selecting "High School" matches plans with grade "High School", "HS", or "High School: <concept>")
  const getGradeOptions = () => [
    'K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12',
    'Middle School',
    'High School',
  ];

  // Apply filters and sorting
  const filteredCompletedPlans = sortPlans(filterPlans(completedPlans));
  const filteredInProgressPlans = sortPlans(filterPlans(inProgressPlans));
  
  const recentCompletedPlans = filteredCompletedPlans.slice(0, 6);
  const recentInProgressPlans = filteredInProgressPlans.slice(0, 6);
  const recentQuizzes = quizzes.slice(0, 6);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', width: '100%', overflow: 'hidden', background: '#F9FAFF' }}>
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        planTitle={deleteModal.plan?.title || ''}
        language={language}
      />
      <DeleteConfirmationModal
        isOpen={deleteQuizModal.isOpen}
        onClose={handleQuizDeleteCancel}
        onConfirm={handleQuizDeleteConfirm}
        planTitle={deleteQuizModal.quiz?.title || ''}
        language={language}
      />
      {/* No generations remaining modal */}
      {showNoGenerationsModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000,
          }}
          onClick={() => setShowNoGenerationsModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
              maxWidth: '420px',
              width: '90%',
              padding: '1.5rem',
              textAlign: 'center',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: '2rem', color: '#DC2626', marginBottom: '0.75rem' }}>✕</div>
            <h3 style={{ margin: '0 0 0.75rem', fontSize: '1.25rem', fontFamily: 'Baloo 2', color: '#1F2937' }}>
              {t('no_generations_left', language)}
            </h3>
            <p style={{ margin: '0 0 1.25rem', fontFamily: 'Baloo 2', color: '#6B7280', lineHeight: 1.5 }}>
              No generations remaining. Please contact support.
            </p>
            <button
              onClick={() => setShowNoGenerationsModal(false)}
              style={{
                padding: '0.5rem 1.5rem',
                background: '#6366F1',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontFamily: 'Baloo 2',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
      <NavBar />
      <div style={{ display: 'flex', flex: 1, width: '100%', overflow: 'hidden', background: '#F9FAFF' }}>
        {/* Sidebar */}
        <div style={{ width: '18%', minWidth: '200px', maxWidth: '280px', flexShrink: 0, overflowY: 'auto' }}>
          <DashboardSidebar />
        </div>

        {/* Main Content */}
        <div style={{
          flex: 1, 
          padding: '1rem', 
          maxWidth: 'calc(100% - 18%)',
          flexDirection: 'column', 
          gap: '1rem', 
          display: 'flex',
          background: '#F9FAFF',
          overflowY: 'auto',
          overflowX: 'hidden',
          boxSizing: 'border-box'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', width: '100%' }}>
          {/* Dashboard slideshow: Slide 1 = Welcome + Feedback, Slide 2 = Piloting stats */}
          <div style={{ width: '100%', position: 'relative', overflow: 'hidden' }}>
            <div style={{
              display: 'flex',
              width: '100%',
              transition: 'transform 0.3s ease-out',
              transform: `translateX(-${slideIndex * 100}%)`
            }}>
              {/* Slide 1: Welcome + Feedback button */}
              <div style={{
                flex: '0 0 100%',
                width: '100%',
                padding: '1.1rem 1.1rem 1.6rem',
                background: 'white',
                boxShadow: '0px 2px 8px rgba(13, 84, 73, 0.15)',
                borderRadius: '1rem',
                border: '1px solid #62C9FF',
                display: 'flex',
                flexDirection: 'row',
                gap: '1.25rem',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                boxSizing: 'border-box'
              }}>
                <div style={{ flex: '1 1 0', minWidth: '300px', flexDirection: 'column', gap: '1rem', display: 'flex' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                    <div style={{
                      width: '5rem', height: '5rem', minWidth: '5rem', minHeight: '5rem', borderRadius: '50%',
                      background: '#177768', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                    }}>
                      <img src="/assets/leaf.jpg" alt="Kumu Connect Logo" style={{ width: '100%', height: '100%' }} />
                    </div>
                    <div style={{ flex: '1 1 0', minWidth: '200px', flexDirection: 'column', gap: '0.25rem', display: 'flex' }}>
                      <div style={{ color: '#0D5449', fontSize: 'clamp(1.5rem, 2.5vw, 2.5rem)', fontFamily: 'Baloo 2', fontWeight: '700', lineHeight: '1.2' }}>
                        {t('kumu_connect', language)}
                      </div>
                      <div style={{ color: '#4b5563', fontSize: 'clamp(0.9rem, 1.1vw, 1.125rem)', fontFamily: 'Baloo 2', fontWeight: '400' }}>
                        {t('learning_that_connects', language)}
                      </div>
                    </div>
                  </div>
                  <div style={{ width: '100%', flexDirection: 'column', gap: '1rem', display: 'flex' }}>
                    <p style={{ color: '#4b5563', fontSize: 'clamp(1rem, 1.2vw, 1.125rem)', fontFamily: 'Baloo 2', fontWeight: '400', lineHeight: 1.5, margin: 0 }}>
                      {t('welcome_pilot_intro', language)}
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem' }}>
                      <button
                        type="button"
                        onClick={handleDashboardFeedback}
                        style={{
                          padding: '0.5rem 1rem 0.5rem 0.75rem',
                          background: 'linear-gradient(131deg, #33A3FF 16%, #5773AE 100%)',
                          border: 'none',
                          borderRadius: '0.5rem',
                          color: 'white',
                          fontSize: '0.875rem',
                          fontFamily: 'Azeret Mono',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          whiteSpace: 'nowrap',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.95'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                      >
                        <img src="/assets/light_bulb.svg" alt="" style={{ width: '1.25rem', height: '1.25rem', objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
                        {t('feedback', language)}
                      </button>
                      <button
                        type="button"
                        onClick={() => alert('Beta testing info coming soon!')}
                        style={{
                          padding: '0.5rem 1rem',
                          borderRadius: '0.5rem',
                          border: '1px solid #62C9FF',
                          background: '#FFF',
                          color: '#5773AE',
                          fontSize: '0.875rem',
                          fontFamily: 'Azeret Mono',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#F8FAFC'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = '#FFF'; }}
                      >
                        {t('read_about_beta_btn', language)} &gt;
                      </button>
                    </div>
                  </div>
                </div>
                <div style={{ flex: '0 1 440px', width: '100%', minWidth: '260px', maxWidth: '440px', aspectRatio: '16/9', borderRadius: '0.75rem', overflow: 'hidden', background: '#D9D9D9', marginLeft: 'auto' }}>
                  <iframe
                    title="Kumu Connect Intro Video"
                    src="https://www.youtube-nocookie.com/embed/r4VJ1IFW0I4?rel=0&modestbranding=1&playsinline=1"
                    style={{ width: '100%', height: '100%', border: '0', display: 'block' }}
                    loading="lazy"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>

              {/* Slide 2: Kumu Connect Pilot Testing (Figma 620-12871) - three columns */}
              <div style={{
                flex: '0 0 100%',
                width: '100%',
                padding: '1.1rem 1.1rem 1.6rem',
                background: 'white',
                boxShadow: '0px 2px 8px rgba(13, 84, 73, 0.15)',
                borderRadius: '1rem',
                border: '1px solid #62C9FF',
                display: 'flex',
                flexDirection: 'row',
                gap: '1.5rem',
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                boxSizing: 'border-box'
              }}>
                {/* Left column: Pilot Testing info */}
                <div style={{ flex: '1 1 280px', minWidth: '0', display: 'flex', flexDirection: 'column', gap: '3rem' }}>
                  <div>
                    <div style={{ color: '#374151', fontSize: 'clamp(1.25rem, 1.75vw, 1.75rem)', fontFamily: 'Baloo 2', fontWeight: '700', marginBottom: '0.75rem', lineHeight: 1.3 }}>
                      {t('pilot_testing_title', language)}
                    </div>
                    <p style={{ color: '#4b5563', fontSize: 'clamp(0.9375rem, 1.1vw, 1.0625rem)', fontFamily: 'Baloo 2', fontWeight: '400', lineHeight: 1.5, margin: 0 }}>
                      {t('pilot_testing_intro', language)}
                    </p>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                      <img src="/assets/Gift_Icon.png" alt="" style={{ width: '2rem', height: '2rem', flexShrink: 0, marginTop: '0.1rem' }} />
                      <div>
                        <div style={{ color: '#374151', fontSize: '1.0625rem', fontFamily: 'Baloo 2', fontWeight: '700', marginBottom: '0.25rem' }}>{t('earn_free_creations', language)}</div>
                        <p style={{ color: '#4b5563', fontSize: '0.9375rem', fontFamily: 'Baloo 2', fontWeight: '400', lineHeight: 1.5, margin: 0 }}>{t('earn_free_creations_desc', language)}</p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                      <img src="/assets/Gift_Icon.png" alt="" style={{ width: '2rem', height: '2rem', flexShrink: 0, marginTop: '0.1rem' }} />
                      <div>
                        <div style={{ color: '#374151', fontSize: '1.0625rem', fontFamily: 'Baloo 2', fontWeight: '700', marginBottom: '0.25rem' }}>{t('get_rewarded', language)}</div>
                        <p style={{ color: '#4b5563', fontSize: '0.9375rem', fontFamily: 'Baloo 2', fontWeight: '400', lineHeight: 1.5, margin: 0 }}>{t('get_rewarded_desc', language)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Middle column: What kind of feedback */}
                <div style={{ flex: '1 1 260px', minWidth: '0', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div>
                    <div style={{ color: '#374151', fontSize: 'clamp(1.25rem, 1.75vw, 1.75rem)', fontFamily: 'Baloo 2', fontWeight: '700', marginBottom: '0.5rem', lineHeight: 1.3 }}>
                      {t('what_kind_of_feedback', language)}
                    </div>
                    <p style={{ color: '#4b5563', fontSize: 'clamp(0.9375rem, 1.1vw, 1rem)', fontFamily: 'Baloo 2', fontWeight: '400', lineHeight: 1.5, margin: 0 }}>
                      {t('what_kind_of_feedback_intro', language)}
                    </p>
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <img src="/assets/Bug_Icon.png" alt="" style={{ width: '2rem', height: '2rem', flexShrink: 0, objectFit: 'contain' }} />
                      <span style={{ color: '#374151', fontSize: '1rem', fontFamily: 'Baloo 2', fontWeight: '500' }}>{t('report_bugs', language)}</span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <img src="/assets/Translation_Icon.png" alt="" style={{ width: '2rem', height: '2rem', flexShrink: 0, objectFit: 'contain' }} />
                      <span style={{ color: '#374151', fontSize: '1rem', fontFamily: 'Baloo 2', fontWeight: '500' }}>{t('translation_suggestions', language)}</span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <img src="/assets/FeatureRequest_Icon.png" alt="" style={{ width: '2rem', height: '2rem', flexShrink: 0, objectFit: 'contain' }} />
                      <span style={{ color: '#374151', fontSize: '1rem', fontFamily: 'Baloo 2', fontWeight: '500' }}>{t('feature_requests', language)}</span>
                    </li>
                  </ul>
                </div>

                {/* Right column: Your Piloting Stats + Feedback Survey button */}
                <div style={{ flex: '0 0 auto', width: '100%', maxWidth: '320px', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{
                    padding: '1.25rem',
                    background: 'white',
                    borderRadius: '0.75rem',
                    border: '2px solid #A6D9F7',
                    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.06)',
                    boxSizing: 'border-box'
                  }}>
                    <div style={{ color: '#374151', fontSize: '1.125rem', fontFamily: 'Baloo 2', fontWeight: '700', marginBottom: '1rem' }}>
                      {t('your_piloting_stats', language)}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                        padding: '0.375rem 0.5rem 0.375rem 0.375rem',
                        background: '#C2F5FF', border: '1px solid #A6D9F7', borderRadius: '0.5rem',
                        width: 'fit-content'
                      }}>
                        <span style={{
                          minWidth: '1.75rem', padding: '0.2rem 0.4rem',
                          background: '#FFF',
                          border: '1px solid #62C9FF',
                          borderRadius: '4px',
                          boxShadow: '0 2px 2px 0 rgba(0, 0, 0, 0.25) inset',
                          fontSize: '0.9375rem', fontFamily: 'Baloo 2', fontWeight: '700', color: '#0D5449', textAlign: 'center', lineHeight: 1.2
                        }}>
                          {user && (user.generations_remaining !== undefined && user.generations_remaining !== null) ? user.generations_remaining : '--'}
                        </span>
                        <span style={{ color: '#5773AE', fontSize: '10px', fontFamily: 'Azeret Mono', fontWeight: '600', lineHeight: '120%', textTransform: 'uppercase' }}>
                          {t('creations_left', language)}
                        </span>
                      </div>
                      <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                        padding: '0.375rem 0.5rem 0.375rem 0.375rem',
                        background: '#C2F5FF', border: '1px solid #A6D9F7', borderRadius: '0.5rem',
                        width: 'fit-content'
                      }}>
                        <span style={{
                          minWidth: '1.75rem', padding: '0.2rem 0.4rem',
                          background: '#FFF',
                          border: '1px solid #62C9FF',
                          borderRadius: '4px',
                          boxShadow: '0 2px 2px 0 rgba(0, 0, 0, 0.25) inset',
                          fontSize: '0.9375rem', fontFamily: 'Baloo 2', fontWeight: '700', color: '#0D5449', textAlign: 'center', lineHeight: 1.2
                        }}>
                          {user && (user.feedback_count !== undefined && user.feedback_count !== null) ? user.feedback_count : '--'}
                        </span>
                        <span style={{ color: '#5773AE', fontSize: '10px', fontFamily: 'Azeret Mono', fontWeight: '600', lineHeight: '120%', textTransform: 'uppercase' }}>
                          {t('feedbacks_submitted', language)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleDashboardFeedback}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: 'linear-gradient(131deg, #33A3FF 16%, #5773AE 100%)',
                      border: 'none',
                      borderRadius: '0.5rem',
                      color: 'white',
                      fontSize: '0.875rem',
                      fontFamily: 'Azeret Mono',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      letterSpacing: '0.02em',
                      boxShadow: '0px 2px 4px rgba(0,0,0,0.1)'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.95'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                  >
                    <img src="/assets/light_bulb.svg" alt="" style={{ width: '1.25rem', height: '1.25rem', filter: 'brightness(0) invert(1)' }} />
                    {t('feedback_survey_btn', language)}
                  </button>
                </div>
              </div>
            </div>
            {/* Slide indicators */}
            <div style={{
              position: 'absolute',
              left: '50%',
              bottom: '0.45rem',
              transform: 'translateX(-50%)',
              display: 'flex',
              justifyContent: 'center',
              gap: '0.5rem',
              zIndex: 5
            }}>
              {[0, 1].map((i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={i === 0 ? 'Welcome slide' : 'Piloting stats slide'}
                  onClick={() => setSlideIndex(i)}
                  style={{
                    width: '0.5rem',
                    height: '0.5rem',
                    borderRadius: '50%',
                    border: 'none',
                    padding: 0,
                    background: slideIndex === i ? '#33A3FF' : '#BFBBB9',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                />
              ))}
            </div>
          </div>

          {/* Action Cards Section */}
          <div style={{
            width: '100%', 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1.5rem'
          }}>
            {/* Create Lesson Plan Card */}
            <div 
              onClick={() => {
                if (user != null && (user.generations_remaining === undefined ? 20 : user.generations_remaining) <= 0) {
                  setShowNoGenerationsModal(true);
                  return;
                }
                track('Clicked Create Lesson Plan', { screen_name: 'Dashboard' });
                navigate('/create-lesson-plan/form');
              }}
              style={{
                padding: '1.25rem', 
                background: '#F9FAFF', 
                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)', 
                borderRadius: '0.75rem', 
                border: '1px solid #B780EC', 
                flexDirection: 'column', 
                gap: '0.75rem', 
                display: 'flex',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0px 4px 12px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0px 2px 8px rgba(0, 0, 0, 0.1)';
              }}
            >
              <div style={{width: '100%', flexDirection: 'column', gap: '0.5rem', display: 'flex'}}>
                <div style={{
                  color: '#B14AED', 
                  fontSize: 'clamp(1.125rem, 1.5vw, 1.5rem)', 
                  fontFamily: 'Baloo 2', 
                  fontWeight: '600', 
                  textTransform: 'capitalize'
                }}>
                  {t('create_lesson_plan', language)}
                </div>
                <div style={{
                  color: '#322F2D', 
                  fontSize: 'clamp(0.9rem, 1.1vw, 1.125rem)', 
                  fontFamily: 'Baloo 2', 
                  fontWeight: '400', 
                  lineHeight: '1.5'
                }}>
                  {t('create_lesson_plan_desc', language)}
                </div>
              </div>
              <div style={{width: '100%', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                <div data-property-1="AI" style={{
                  padding: '0.25rem 0.5rem', 
                  background: '#E4D8FF', 
                  borderRadius: '0.25rem', 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}>
                  <img
                    src="/assets/Solid/Interface/AI.svg"
                    alt="AI Assisted"
                    style={{ width: '1.25rem', height: '1.25rem', flexShrink: 0 }}
                  />
                  <div style={{
                    color: '#B14AED', 
                    fontSize: '0.75rem', 
                    fontFamily: 'Azeret Mono', 
                    fontWeight: '600', 
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap'
                  }}>
                    {t('ai_assisted', language)}
                  </div>
                </div>
                <img
                  src="/assets/info_icon.svg"
                  alt="Info"
                  style={{ width: '1.25rem', height: '1.25rem', flexShrink: 0 }}
                />
              </div>
            </div>

            {/* HIDDEN: Generate Quiz Card temporarily disabled */}
            {/* <div 
              onClick={() => {
                if (user != null && (user.generations_remaining === undefined ? 20 : user.generations_remaining) <= 0) {
                  setShowNoGenerationsModal(true);
                  return;
                }
                navigate('/generate-quiz');
              }}
              style={{
                padding: '1.5rem', 
                background: '#F9FAFF', 
                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)', 
                borderRadius: '0.75rem', 
                border: '1px solid #6366F1', 
                flexDirection: 'column', 
                gap: '1rem', 
                display: 'flex',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0px 4px 12px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0px 2px 8px rgba(0, 0, 0, 0.1)';
              }}
            >
              <div style={{width: '100%', flexDirection: 'column', gap: '0.5rem', display: 'flex'}}>
                <div style={{
                  color: '#6366F1', 
                  fontSize: 'clamp(1.125rem, 1.5vw, 1.5rem)', 
                  fontFamily: 'Baloo 2', 
                  fontWeight: '600', 
                  textTransform: 'capitalize'
                }}>
                  Generate Quiz
                </div>
                <div style={{
                  color: '#322F2D', 
                  fontSize: 'clamp(0.9rem, 1.1vw, 1.125rem)', 
                  fontFamily: 'Baloo 2', 
                  fontWeight: '400', 
                  lineHeight: '1.5'
                }}>
                  Create summative assessments from your lesson plans
                </div>
              </div>
              <div style={{width: '100%', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                <div data-property-1="AI" style={{
                  padding: '0.25rem 0.5rem', 
                  background: '#EEF2FF', 
                  borderRadius: '0.25rem', 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}>
                  <img
                    src="/assets/Solid/Interface/AI.svg"
                    alt="AI Assisted"
                    style={{ width: '1.25rem', height: '1.25rem', flexShrink: 0 }}
                  />
                  <div style={{
                    color: '#6366F1', 
                    fontSize: '0.75rem', 
                    fontFamily: 'Azeret Mono', 
                    fontWeight: '600', 
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap'
                  }}>
                    {t('ai_assisted', language)}
                  </div>
                </div>
                <img
                  src="/assets/info_icon.svg"
                  alt="Info"
                  style={{ width: '1.25rem', height: '1.25rem', flexShrink: 0 }}
                />
              </div>
            </div> */}

            {/* HIDDEN: Augment Lessons Plan Card temporarily disabled */}
            {/* <div 
              onClick={() => alert('Augment Lessons Plan coming soon!')}
              style={{
                padding: '1.5rem', 
                background: '#F9FAFF', 
                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)', 
                borderRadius: '0.75rem', 
                border: '1px solid #B780EC', 
                flexDirection: 'column', 
                gap: '1rem', 
                display: 'flex',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0px 4px 12px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0px 2px 8px rgba(0, 0, 0, 0.1)';
              }}
            >
              <div style={{width: '100%', flexDirection: 'column', gap: '0.5rem', display: 'flex'}}>
                <div style={{
                  color: '#B14AED', 
                  fontSize: 'clamp(1.125rem, 1.5vw, 1.5rem)', 
                  fontFamily: 'Baloo 2', 
                  fontWeight: '600', 
                  textTransform: 'capitalize'
                }}>
                  {t('augment_lessons_plan', language)}
                </div>
                <div style={{
                  color: '#322F2D', 
                  fontSize: 'clamp(0.9rem, 1.1vw, 1.125rem)', 
                  fontFamily: 'Baloo 2', 
                  fontWeight: '400', 
                  lineHeight: '1.5'
                }}>
                  {t('augment_lessons_plan_desc', language)}
                </div>
              </div>
              <div style={{width: '100%', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                <div data-property-1="AI" style={{
                  padding: '0.25rem 0.5rem', 
                  background: '#E4D8FF', 
                  borderRadius: '0.25rem', 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}>
                  <img
                    src="/assets/Solid/Interface/AI.svg"
                    alt="AI Assisted"
                    style={{ width: '1.25rem', height: '1.25rem', flexShrink: 0 }}
                  />
                  <div style={{
                    color: '#B14AED', 
                    fontSize: '0.75rem', 
                    fontFamily: 'Azeret Mono', 
                    fontWeight: '600', 
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap'
                  }}>
                    {t('ai_assisted', language)}
                  </div>
                </div>
                <img
                  src="/assets/info_icon.svg"
                  alt="Info"
                  style={{ width: '1.25rem', height: '1.25rem', flexShrink: 0 }}
                />
              </div>
            </div> */}
          </div>

          {/* Recent Lesson Plans Section */}
          <div style={{
            width: '100%', 
            padding: '1.5rem', 
            background: 'white', 
            boxShadow: '0px 2px 8px rgba(13, 84, 73, 0.15)', 
            borderRadius: '1rem', 
            border: '1px solid #2EC974', 
            flexDirection: 'column', 
            gap: '1.25rem', 
            display: 'flex'
          }}>
            <div style={{width: '100%', flexDirection: 'column', gap: '1rem', display: 'flex'}}>
              <div style={{
                color: '#0D5449', 
                fontSize: 'clamp(1.125rem, 1.5vw, 1.5rem)', 
                fontFamily: 'Baloo 2', 
                fontWeight: '600', 
                textTransform: 'capitalize'
              }}>
                {t('recent_lesson_plans', language)}
              </div>
              <div style={{width: '100%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem'}}>
                {/* Filters + Clear all grouped together on the left */}
                <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', position: 'relative'}}>
                  {/* Subject Filter */}
                  <div style={{position: 'relative'}} data-filter-dropdown>
                    <div
                      style={{
                        padding: '0.5rem 0.5rem 0.5rem 1rem',
                        background: filters.subject ? 'linear-gradient(131deg, #137A55 16%, #0D5449 100%)' : 'linear-gradient(131deg, #189F61 16%, #137A55 100%)',
                        borderRadius: '0.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        cursor: 'pointer',
                        transition: 'opacity 0.2s',
                        position: 'relative',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    >
                      <div
                        onClick={() => {
                          setShowSubjectDropdown(!showSubjectDropdown);
                          setShowLanguageDropdown(false);
                          setShowGradeDropdown(false);
                          setShowSortDropdown(false);
                        }}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}
                      >
                        <div style={{
                          color: 'white',
                          fontSize: 'clamp(0.875rem, 1vw, 1rem)',
                          fontFamily: 'Azeret Mono',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          whiteSpace: 'nowrap',
                        }}>
                          {filters.subject ? (filters.subject === 'cs' ? 'CS' : filters.subject === 'math' ? 'MATH' : 'SCIENCE') : t('subject', language)}
                        </div>
                        <div style={{width: '1rem', height: '1rem', position: 'relative', flexShrink: 0}}>
                          <div style={{
                            width: '40%',
                            height: '23%',
                            left: '30%',
                            top: showSubjectDropdown ? '60%' : '36%',
                            position: 'absolute',
                            background: 'white',
                            clipPath: showSubjectDropdown ? 'polygon(0 100%, 100% 100%, 50% 0)' : 'polygon(0 0, 100% 0, 50% 100%)',
                            transition: 'all 0.2s'
                          }} />
                        </div>
                      </div>
                      {filters.subject && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setFilters({...filters, subject: null}); setShowSubjectDropdown(false); }}
                          style={{
                            width: '1.25rem',
                            height: '1.25rem',
                            borderRadius: '50%',
                            background: 'rgba(255,255,255,0.3)',
                            border: 'none',
                            color: 'white',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1rem',
                            lineHeight: 1,
                            padding: 0,
                            flexShrink: 0,
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.5)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.3)'; }}
                          aria-label="Clear subject filter"
                        >
                          ×
                        </button>
                      )}
                    </div>
                    {showSubjectDropdown && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        marginTop: '0.25rem',
                        background: 'white',
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                        borderRadius: '0.5rem',
                        border: '1px solid #E2E3E9',
                        minWidth: '150px',
                        zIndex: 1000,
                        padding: '0.25rem 0',
                        maxHeight: '300px',
                        overflowY: 'auto'
                      }}>
                        <div
                          onClick={() => {
                            setFilters({...filters, subject: null});
                            setShowSubjectDropdown(false);
                          }}
                          style={{
                            padding: '0.5rem 1rem',
                            cursor: 'pointer',
                            color: filters.subject === null ? '#189F61' : '#666',
                            fontSize: '0.875rem',
                            fontFamily: 'Baloo 2',
                            fontWeight: filters.subject === null ? '600' : '400',
                            backgroundColor: filters.subject === null ? '#F0FDF4' : 'transparent'
                          }}
                          onMouseEnter={(e) => {
                            if (filters.subject !== null) e.currentTarget.style.backgroundColor = '#F9FAFB';
                          }}
                          onMouseLeave={(e) => {
                            if (filters.subject !== null) e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          {t('all', language) || 'All'}
                        </div>
                        {getUniqueSubjects().map(subject => (
                          <div
                            key={subject}
                            onClick={() => {
                              setFilters({...filters, subject});
                              setShowSubjectDropdown(false);
                            }}
                            style={{
                              padding: '0.5rem 1rem',
                              cursor: 'pointer',
                              color: filters.subject === subject ? '#189F61' : '#666',
                              fontSize: '0.875rem',
                              fontFamily: 'Baloo 2',
                              fontWeight: filters.subject === subject ? '600' : '400',
                              backgroundColor: filters.subject === subject ? '#F0FDF4' : 'transparent'
                            }}
                            onMouseEnter={(e) => {
                              if (filters.subject !== subject) e.currentTarget.style.backgroundColor = '#F9FAFB';
                            }}
                            onMouseLeave={(e) => {
                              if (filters.subject !== subject) e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            {subject === 'cs' ? 'Computer Science' : subject === 'math' ? 'Mathematics' : 'Science'}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Language Filter */}
                  <div style={{position: 'relative'}} data-filter-dropdown>
                    <div
                      style={{
                        padding: '0.5rem 0.5rem 0.5rem 1rem',
                        background: filters.language ? 'linear-gradient(131deg, #137A55 16%, #0D5449 100%)' : 'linear-gradient(131deg, #189F61 16%, #137A55 100%)',
                        borderRadius: '0.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        cursor: 'pointer',
                        transition: 'opacity 0.2s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    >
                      <div
                        onClick={() => {
                          setShowLanguageDropdown(!showLanguageDropdown);
                          setShowSubjectDropdown(false);
                          setShowGradeDropdown(false);
                          setShowSortDropdown(false);
                        }}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}
                      >
                        <div style={{
                          color: 'white',
                          fontSize: 'clamp(0.875rem, 1vw, 1rem)',
                          fontFamily: 'Azeret Mono',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          whiteSpace: 'nowrap',
                        }}>
                          {filters.language ? (filters.language === 'en' ? 'ENGLISH' : 'ʻŌLELO HAWAIʻI') : t('language', language)}
                        </div>
                        <div style={{width: '1rem', height: '1rem', position: 'relative', flexShrink: 0}}>
                          <div style={{
                            width: '40%',
                            height: '23%',
                            left: '30%',
                            top: showLanguageDropdown ? '60%' : '36%',
                            position: 'absolute',
                            background: 'white',
                            clipPath: showLanguageDropdown ? 'polygon(0 100%, 100% 100%, 50% 0)' : 'polygon(0 0, 100% 0, 50% 100%)',
                            transition: 'all 0.2s'
                          }} />
                        </div>
                      </div>
                      {filters.language && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setFilters({...filters, language: null}); setShowLanguageDropdown(false); }}
                          style={{
                            width: '1.25rem',
                            height: '1.25rem',
                            borderRadius: '50%',
                            background: 'rgba(255,255,255,0.3)',
                            border: 'none',
                            color: 'white',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1rem',
                            lineHeight: 1,
                            padding: 0,
                            flexShrink: 0,
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.5)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.3)'; }}
                          aria-label="Clear language filter"
                        >
                          ×
                        </button>
                      )}
                    </div>
                    {showLanguageDropdown && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        marginTop: '0.25rem',
                        background: 'white',
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                        borderRadius: '0.5rem',
                        border: '1px solid #E2E3E9',
                        minWidth: '150px',
                        zIndex: 1000,
                        padding: '0.25rem 0'
                      }}>
                        <div
                          onClick={() => {
                            setFilters({...filters, language: null});
                            setShowLanguageDropdown(false);
                          }}
                          style={{
                            padding: '0.5rem 1rem',
                            cursor: 'pointer',
                            color: filters.language === null ? '#189F61' : '#666',
                            fontSize: '0.875rem',
                            fontFamily: 'Baloo 2',
                            fontWeight: filters.language === null ? '600' : '400',
                            backgroundColor: filters.language === null ? '#F0FDF4' : 'transparent'
                          }}
                          onMouseEnter={(e) => {
                            if (filters.language !== null) e.currentTarget.style.backgroundColor = '#F9FAFB';
                          }}
                          onMouseLeave={(e) => {
                            if (filters.language !== null) e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          {t('all', language) || 'All'}
                        </div>
                        {getLanguageOptions().map(option => (
                          <div
                            key={option.value}
                            onClick={() => {
                              setFilters({...filters, language: option.value});
                              setShowLanguageDropdown(false);
                            }}
                            style={{
                              padding: '0.5rem 1rem',
                              cursor: 'pointer',
                              color: filters.language === option.value ? '#189F61' : '#666',
                              fontSize: '0.875rem',
                              fontFamily: 'Baloo 2',
                              fontWeight: filters.language === option.value ? '600' : '400',
                              backgroundColor: filters.language === option.value ? '#F0FDF4' : 'transparent'
                            }}
                            onMouseEnter={(e) => {
                              if (filters.language !== option.value) e.currentTarget.style.backgroundColor = '#F9FAFB';
                            }}
                            onMouseLeave={(e) => {
                              if (filters.language !== option.value) e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            {option.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Grade Filter */}
                  <div style={{position: 'relative'}} data-filter-dropdown>
                    <div
                      style={{
                        padding: '0.5rem 0.5rem 0.5rem 1rem',
                        background: filters.grade ? 'linear-gradient(131deg, #137A55 16%, #0D5449 100%)' : 'linear-gradient(131deg, #189F61 16%, #137A55 100%)',
                        borderRadius: '0.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        cursor: 'pointer',
                        transition: 'opacity 0.2s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    >
                      <div
                        onClick={() => {
                          setShowGradeDropdown(!showGradeDropdown);
                          setShowSubjectDropdown(false);
                          setShowLanguageDropdown(false);
                          setShowSortDropdown(false);
                        }}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}
                      >
                        <div style={{
                          color: 'white',
                          fontSize: 'clamp(0.875rem, 1vw, 1rem)',
                          fontFamily: 'Azeret Mono',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          whiteSpace: 'nowrap',
                        }}>
                          {filters.grade || t('grade', language)}
                        </div>
                        <div style={{width: '1rem', height: '1rem', position: 'relative', flexShrink: 0}}>
                          <div style={{
                            width: '40%',
                            height: '23%',
                            left: '30%',
                            top: showGradeDropdown ? '60%' : '36%',
                            position: 'absolute',
                            background: 'white',
                            clipPath: showGradeDropdown ? 'polygon(0 100%, 100% 100%, 50% 0)' : 'polygon(0 0, 100% 0, 50% 100%)',
                            transition: 'all 0.2s'
                          }} />
                        </div>
                      </div>
                      {filters.grade && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setFilters({...filters, grade: null}); setShowGradeDropdown(false); }}
                          style={{
                            width: '1.25rem',
                            height: '1.25rem',
                            borderRadius: '50%',
                            background: 'rgba(255,255,255,0.3)',
                            border: 'none',
                            color: 'white',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1rem',
                            lineHeight: 1,
                            padding: 0,
                            flexShrink: 0,
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.5)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.3)'; }}
                          aria-label="Clear grade filter"
                        >
                          ×
                        </button>
                      )}
                    </div>
                    {showGradeDropdown && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        marginTop: '0.25rem',
                        background: 'white',
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                        borderRadius: '0.5rem',
                        border: '1px solid #E2E3E9',
                        minWidth: '150px',
                        zIndex: 1000,
                        padding: '0.25rem 0',
                        maxHeight: '300px',
                        overflowY: 'auto'
                      }}>
                        <div
                          onClick={() => {
                            setFilters({...filters, grade: null});
                            setShowGradeDropdown(false);
                          }}
                          style={{
                            padding: '0.5rem 1rem',
                            cursor: 'pointer',
                            color: filters.grade === null ? '#189F61' : '#666',
                            fontSize: '0.875rem',
                            fontFamily: 'Baloo 2',
                            fontWeight: filters.grade === null ? '600' : '400',
                            backgroundColor: filters.grade === null ? '#F0FDF4' : 'transparent'
                          }}
                          onMouseEnter={(e) => {
                            if (filters.grade !== null) e.currentTarget.style.backgroundColor = '#F9FAFB';
                          }}
                          onMouseLeave={(e) => {
                            if (filters.grade !== null) e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          {t('all', language) || 'All'}
                        </div>
                        {getGradeOptions().map(grade => (
                          <div
                            key={grade}
                            onClick={() => {
                              setFilters({...filters, grade});
                              setShowGradeDropdown(false);
                            }}
                            style={{
                              padding: '0.5rem 1rem',
                              cursor: 'pointer',
                              color: filters.grade === grade ? '#189F61' : '#666',
                              fontSize: '0.875rem',
                              fontFamily: 'Baloo 2',
                              fontWeight: filters.grade === grade ? '600' : '400',
                              backgroundColor: filters.grade === grade ? '#F0FDF4' : 'transparent'
                            }}
                            onMouseEnter={(e) => {
                              if (filters.grade !== grade) e.currentTarget.style.backgroundColor = '#F9FAFB';
                            }}
                            onMouseLeave={(e) => {
                              if (filters.grade !== grade) e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            {grade}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {(filters.subject || filters.language || filters.grade) && (
                    <button
                      type="button"
                      onClick={() => {
                        setFilters({ subject: null, language: null, grade: null });
                        setShowSubjectDropdown(false);
                        setShowLanguageDropdown(false);
                        setShowGradeDropdown(false);
                      }}
                      style={{
                        padding: '0.5rem 0.75rem',
                        background: 'transparent',
                        border: '1px solid #189F61',
                        borderRadius: '0.5rem',
                        color: '#137A55',
                        fontSize: '0.8125rem',
                        fontFamily: 'Baloo 2',
                        fontWeight: '600',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#F0FDF4'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      Clear all filters
                    </button>
                  )}
                </div>
                {/* Sort - right side */}
                <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', position: 'relative', flexShrink: 0, marginLeft: 'auto'}}>
                  <div style={{
                    color: '#696A6E', 
                    fontSize: 'clamp(0.875rem, 1vw, 1rem)', 
                    fontFamily: 'Azeret Mono', 
                    fontWeight: '600', 
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap'
                  }}>
                    {t('sort_by', language)}
                  </div>
                  <div 
                    style={{display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', position: 'relative'}}
                    data-sort-dropdown
                    onClick={() => {
                      setShowSortDropdown(!showSortDropdown);
                      setShowSubjectDropdown(false);
                      setShowLanguageDropdown(false);
                      setShowGradeDropdown(false);
                    }}
                  >
                    <div style={{
                      color: '#1E9481', 
                      fontSize: 'clamp(0.875rem, 1vw, 1rem)', 
                      fontFamily: 'Azeret Mono', 
                      fontWeight: '600', 
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap'
                    }}>
                      {sortBy === 'most_recent' ? (t('most_recent', language) || 'Most Recent') :
                       sortBy === 'oldest' ? (t('oldest', language) || 'Oldest') :
                       sortBy === 'title_asc' ? (t('title_a_z', language) || 'Title A-Z') :
                       sortBy === 'title_desc' ? (t('title_z_a', language) || 'Title Z-A') :
                       sortBy === 'status' ? (t('status', language) || 'Status') :
                       t('priority', language)}
                    </div>
                    <div style={{width: '1.25rem', height: '1.25rem', position: 'relative', flexShrink: 0}}>
                      <div style={{
                        width: '40%', 
                        height: '23%', 
                        left: '30%', 
                        top: showSortDropdown ? '60%' : '36%', 
                        position: 'absolute', 
                        background: '#1E9481',
                        clipPath: showSortDropdown ? 'polygon(0 100%, 100% 100%, 50% 0)' : 'polygon(0 0, 100% 0, 50% 100%)',
                        transition: 'all 0.2s'
                      }} />
                    </div>
                    {showSortDropdown && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: '0.25rem',
                        background: 'white',
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                        borderRadius: '0.5rem',
                        border: '1px solid #E2E3E9',
                        minWidth: '180px',
                        zIndex: 1000,
                        padding: '0.25rem 0'
                      }}>
                        {[
                          { value: 'most_recent', label: t('most_recent', language) || 'Most Recent' },
                          { value: 'oldest', label: t('oldest', language) || 'Oldest' },
                          { value: 'title_asc', label: t('title_a_z', language) || 'Title A-Z' },
                          { value: 'title_desc', label: t('title_z_a', language) || 'Title Z-A' },
                          { value: 'status', label: t('status', language) || 'Status' }
                        ].map(option => (
                          <div
                            key={option.value}
                            onClick={() => {
                              setSortBy(option.value);
                              setShowSortDropdown(false);
                            }}
                            style={{
                              padding: '0.5rem 1rem',
                              cursor: 'pointer',
                              color: sortBy === option.value ? '#189F61' : '#666',
                              fontSize: '0.875rem',
                              fontFamily: 'Baloo 2',
                              fontWeight: sortBy === option.value ? '600' : '400',
                              backgroundColor: sortBy === option.value ? '#F0FDF4' : 'transparent'
                            }}
                            onMouseEnter={(e) => {
                              if (sortBy !== option.value) e.currentTarget.style.backgroundColor = '#F9FAFB';
                            }}
                            onMouseLeave={(e) => {
                              if (sortBy !== option.value) e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            {option.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Lesson Plans Grid - Separated by Status */}
            {loading ? (
              <div style={{padding: '3rem', textAlign: 'center', color: '#666', width: '100%'}}>
                Loading lesson plans...
              </div>
            ) : (
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  {/* Completed Lesson Plans Section */}
                  {recentCompletedPlans.length > 0 && (
                    <div style={{width: '100%', flexDirection: 'column', gap: '1rem', display: 'flex'}}>
                      <div style={{
                        color: '#0D5449', 
                        fontSize: 'clamp(1rem, 1.25vw, 1.25rem)', 
                        fontFamily: 'Baloo 2', 
                        fontWeight: '600', 
                        textTransform: 'capitalize'
                      }}>
                        {t('completed_lesson_plans', language)}
                      </div>
                      <div style={{
                        width: '100%', 
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                        gap: '1.5rem'
                      }}>
                        {recentCompletedPlans.map(plan => (
                          <LessonPlanCard 
                            key={plan.id} 
                            plan={plan} 
                            onClick={() => handleCardClick(plan)}
                            onDeleteClick={handleDeleteClick}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* In Progress Lesson Plans Section */}
                  {recentInProgressPlans.length > 0 && (
                    <div style={{width: '100%', flexDirection: 'column', gap: '1rem', display: 'flex'}}>
                      <div style={{
                        color: '#0D5449', 
                        fontSize: 'clamp(1rem, 1.25vw, 1.25rem)', 
                        fontFamily: 'Baloo 2', 
                        fontWeight: '600', 
                        textTransform: 'capitalize'
                      }}>
                        {t('in_progress_lesson_plans', language)}
                      </div>
                      <div style={{
                        width: '100%', 
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                        gap: '1.5rem'
                      }}>
                        {recentInProgressPlans.map(plan => (
                          <LessonPlanCard 
                            key={plan.id} 
                            plan={plan} 
                            onClick={() => handleInProgressCardClick(plan)}
                            onDeleteClick={handleDeleteClick}
                            disabled={resumingPlanId === plan.id}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Empty State */}
                {recentCompletedPlans.length === 0 && recentInProgressPlans.length === 0 && (
                  <div style={{padding: '3rem', textAlign: 'center', color: '#666', width: '100%'}}>
                    <p>No lesson plans yet. Start creating your first lesson plan!</p>
                  </div>
                )}
              </div>
            )}

            {/* View All Button */}
            <div 
              onClick={() => navigate('/generated-material')}
              style={{
                padding: '0.5rem 1rem', 
                borderRadius: '1.5rem', 
                border: '2px solid #189F61', 
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                cursor: 'pointer',
                width: 'fit-content',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(24, 159, 97, 0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{
                color: '#137A55', 
                fontSize: 'clamp(0.875rem, 1vw, 1rem)', 
                fontFamily: 'Azeret Mono', 
                fontWeight: '600', 
                textTransform: 'uppercase',
                whiteSpace: 'nowrap'
              }}>
                {t('view_all_lessons', language)}
              </div>
              <div style={{width: '1.5rem', height: '1.5rem', position: 'relative', flexShrink: 0}}>
                <div style={{
                  width: '40%', 
                  height: '60%', 
                  left: '30%', 
                  top: '20%', 
                  position: 'absolute', 
                  background: '#137A55',
                  clipPath: 'polygon(0 0, 100% 50%, 0 100%)'
                }} />
              </div>
            </div>
          </div>

          {/* HIDDEN: Recent Generated Quizzes Section temporarily disabled */}
          </div>
      </div>
    </div>
    </div>
  );
}

export default function Dashboard() {
  return (
    <ProtectedRoute>
      <DashboardContent />
    </ProtectedRoute>
  );
}
